import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import ApexCharts from "apexcharts";
import { TextField, MenuItem, Button, Stack, InputAdornment } from "@mui/material";
import foodPublicApi from "../api/foodPublicApi";

const highlightNutrients = [
  { key: "calories", number: "208", label: "Calories (kcal)" },
  { key: "protein", number: "203", label: "Protein (g)" },
  { key: "fat", number: "204", label: "Fat (g)" },
  { key: "carbs", number: "205", label: "Carbs (g)" },
  { key: "fiber", number: "291", label: "Fiber (g)" },
  { key: "sodium", number: "307", label: "Sodium (mg)" },
];

const FoodDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [food, setFood] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [unitFilter, setUnitFilter] = useState("all");
  const [moreFoods, setMoreFoods] = useState([]);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await foodPublicApi.getDetail(slug);
        setFood(response);
      } catch (err) {
        const message =
          err?.data?.message ||
          err?.statusText ||
          "Unable to load this ingredient.";
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [slug]);

  useEffect(() => {
    let active = true;
    const fetchMoreFoods = async () => {
      try {
        const randomPage = Math.floor(Math.random() * 40) + 1;
        const response = await foodPublicApi.search({
          limit: 50,
          page: randomPage,
        });
        const items = response?.data || [];
        const filtered = items.filter(
          (item) => item?.slug && item.slug !== (food?.slug || slug)
        );
        const randomized = filtered
          .sort(() => 0.5 - Math.random())
          .slice(0, 10);
        if (active) {
          setMoreFoods(randomized);
        }
      } catch (err) {
        if (active) {
          setMoreFoods([]);
        }
      }
    };
    fetchMoreFoods();
    return () => {
      active = false;
    };
  }, [slug, food?.slug]);

  const nutrientLookup = useMemo(() => {
    if (!food?.foodNutrients) return {};
    return food.foodNutrients.reduce((acc, nutrientEntry) => {
      const number = nutrientEntry?.nutrient?.number;
      if (number) acc[number] = nutrientEntry;
      return acc;
    }, {});
  }, [food]);

  const macroSeries = useMemo(() => {
    const macros = ["208", "203", "204", "205"].map((number) => {
      const nutrient = nutrientLookup[number];
      return nutrient?.amount ? Number(nutrient.amount) : 0;
    });
    return macros;
  }, [nutrientLookup]);

  const calories = nutrientLookup["208"]?.amount ?? null;
  const protein = nutrientLookup["203"]?.amount ?? null;
  const fat = nutrientLookup["204"]?.amount ?? null;
  const carbs = nutrientLookup["205"]?.amount ?? null;
  const fiber = nutrientLookup["291"]?.amount ?? null;
  const sodium = nutrientLookup["307"]?.amount ?? null;
  const unitOptions = useMemo(() => {
    const units = new Set();
    (food?.foodNutrients || []).forEach((nutrient) => {
      if (nutrient?.nutrient?.unitName) {
        units.add(nutrient.nutrient.unitName);
      }
    });
    return Array.from(units).sort();
  }, [food]);
  const filteredNutrients = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (food?.foodNutrients || []).filter((nutrient) => {
      const name = nutrient?.nutrient?.name?.toLowerCase() || "";
      const number = nutrient?.nutrient?.number?.toString().toLowerCase() || "";
      const source =
        nutrient?.foodNutrientDerivation?.description?.toLowerCase() || "";
      const matchesTerm = term
        ? name.includes(term) || number.includes(term) || source.includes(term)
        : true;
      const unitName = nutrient?.nutrient?.unitName || "";
      const matchesUnit =
        unitFilter === "all" ||
        unitName.toLowerCase() === unitFilter.toLowerCase();
      return matchesTerm && matchesUnit;
    });
  }, [food, searchTerm, unitFilter]);
  const totalNutrients = food?.foodNutrients?.length || 0;
  const filteredCount = filteredNutrients.length;

  const handleExport = () => {
    if (!filteredNutrients.length) return;
    const header = ["Nutrient", "Number", "Amount", "Unit", "Source"];
    const rows = filteredNutrients.map((nutrient) => [
      `"${nutrient?.nutrient?.name || ""}"`,
      nutrient?.nutrient?.number || "",
      nutrient?.amount ?? "",
      nutrient?.nutrient?.unitName || "",
      `"${nutrient?.foodNutrientDerivation?.description || ""}"`,
    ]);
    const csvContent = [header, ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `${food?.slug || slug || "caloriespal-food"}-nutrients.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!food) return;
    const options = {
      chart: {
        type: "radar",
        height: 360,
        toolbar: { show: false },
      },
      series: [
        {
          name: "Amount",
          data: macroSeries.map((value) => Number(value.toFixed(2))),
        },
      ],
      labels: ["Calories", "Protein", "Fat", "Carbs"],
      colors: ["#a3e635"],
      yaxis: {
        show: false,
      },
      plotOptions: {
        radar: {
          polygons: {
            strokeColor: "rgba(255,255,255,0.1)",
            fill: {
              colors: ["rgba(255,255,255,0.02)", "transparent"],
            },
          },
        },
      },
      dataLabels: {
        color: "#fff",
        background: { enabled: true, borderRadius: 4 },
      },
      stroke: {
        width: 2,
      },
      fill: {
        opacity: 0.3,
        colors: ["#bef264"],
      },
      grid: {
        show: false,
      },
    };

    const chart = new ApexCharts(
      document.querySelector("#macroChart"),
      options
    );
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [food, macroSeries]);

  const baseUrl = "https://www.caloriespal.com";
  const foodName = food?.description || "Food";
  const canonicalUrl = `${baseUrl}/foods/${food?.slug || slug}`;
  const detailTitle = food
    ? `${foodName} Nutrition Facts | CaloriesPal`
    : "Ingredient Nutrition Facts | CaloriesPal";
  const pageDescription = food
    ? `Comprehensive nutrition facts for ${foodName} including calories, macros, vitamins, minerals, and cost-friendly insights.`
    : "View detailed nutrition profiles for any ingredient using the CaloriesPal public food explorer.";
  const nutritionStructuredData = food
    ? {
        "@context": "https://schema.org",
        "@type": "NutritionInformation",
        name: foodName,
        calories: calories ? `${calories} kcal` : undefined,
        fatContent: fat ? `${fat} g` : undefined,
        carbohydrateContent: carbs ? `${carbs} g` : undefined,
        fiberContent: fiber ? `${fiber} g` : undefined,
        proteinContent: protein ? `${protein} g` : undefined,
        sodiumContent: sodium ? `${sodium} mg` : undefined,
        servingSize:
          food.foodPortions && food.foodPortions.length > 0
            ? `${food.foodPortions[0].gramWeight} g`
            : undefined,
        mainEntityOfPage: canonicalUrl,
      }
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-14 h-14 border-4 border-lime-300 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !food) {
    const fallbackTitle = "Ingredient Nutrition Facts | CaloriesPal";
    const fallbackDescription =
      "Search and explore nutrition data for thousands of foods with the CaloriesPal public explorer.";
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-6 text-center text-white">
        <Helmet>
          <title>{fallbackTitle}</title>
          <meta name="description" content={fallbackDescription} />
          <meta name="robots" content="index, follow" />
          <link rel="canonical" href={canonicalUrl} />
        </Helmet>
        <p className="text-2xl font-semibold mb-4">{error || "Not found"}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-3 rounded-full bg-lime-400 text-slate-950 font-semibold"
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="bg-slate-950 min-h-screen text-white">
      <Helmet>
        <title>{detailTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <meta
          name="keywords"
          content={`${foodName} nutrition facts, ${foodName} calories, ingredient nutrients, CaloriesPal food`}
        />
        <meta property="og:title" content={detailTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${baseUrl}/assets/images/Calories (1).png`} />
        <meta property="og:site_name" content="CaloriesPal" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image:alt" content={`${foodName} nutrition information`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={detailTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={`${baseUrl}/assets/images/Calories (1).png`} />
        <meta name="twitter:site" content="@CaloriesPal" />
        <meta name="twitter:creator" content="@CaloriesPal" />
        <link rel="canonical" href={canonicalUrl} />
        {nutritionStructuredData && (
          <script type="application/ld+json">
            {JSON.stringify(nutritionStructuredData)}
          </script>
        )}
      </Helmet>
      <section className="relative py-16 px-6 md:px-10 bg-gradient-to-br from-slate-900 via-slate-950 to-black overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(52,211,153,0.25),_transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto">
          <div className="flex flex-col gap-4">
            <Link
              to="/foods"
              className="inline-flex items-center text-sm uppercase tracking-[0.4em] text-slate-400 hover:text-white"
            >
              <svg
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                className="mr-2"
              >
                <path d="M10 4l-4 4 4 4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Back to search
            </Link>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight text-white drop-shadow">
              {food.description}
            </h1>
            <div className="flex flex-wrap gap-3 text-sm text-slate-300">
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">
                FDC ID: {food.fdcId}
              </span>
              <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">
                {food.foodClass || "General"}
              </span>
              {food.foodCategory?.description && (
                <span className="px-3 py-1 rounded-full bg-white/5 border border-white/10">
                  {food.foodCategory.description}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 space-y-10">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur">
            <h2 className="text-xl font-semibold mb-6 text-white">
              Macro Overview
            </h2>
            <div id="macroChart" />
          </div>
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur">
            <h2 className="text-xl font-semibold mb-6 text-white">
              Key Nutrient Snapshot
            </h2>
            <div className="grid grid-cols-2 gap-6">
              {highlightNutrients.map((item) => {
                const nutrient = nutrientLookup[item.number];
                return (
                  <div
                    key={item.number}
                    className="p-4 rounded-2xl bg-black/40 border border-white/10"
                  >
                    <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                      {item.label}
                    </p>
                    <p className="mt-2 text-2xl font-bold">
                      {nutrient?.amount ?? "-"}
                    </p>
                    <p className="text-xs text-slate-500">
                      Unit: {nutrient?.nutrient?.unitName || "N/A"}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-3xl backdrop-blur overflow-hidden">
          <div className="px-6 py-5 border-b border-white/10">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-xl font-semibold">Full Nutrient Table</h2>
                  <p className="text-slate-400 text-sm">
                    Showing {filteredCount} of {totalNutrients} nutrients
                  </p>
                </div>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleExport}
                    disabled={!filteredCount}
                    sx={{
                      borderColor: "rgba(255,255,255,0.2)",
                      color: "white",
                      "&:hover": { borderColor: "#a3e635", color: "#d9f99d" },
                    }}
                  >
                    Export CSV
                  </Button>
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-2 rounded-full border border-white/10 text-sm font-semibold hover:border-lime-300 hover:text-lime-300 transition"
                  >
                    Print
                  </button>
                </Stack>
              </div>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <div className="flex-1 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">🔎</span>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-full bg-white/90 border border-transparent py-3 pl-12 pr-4 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-lime-400"
                    placeholder="Search nutrient names, numbers, or sources"
                  />
                </div>
                <div className="flex items-center gap-2 rounded-full bg-white/90 px-4 py-2">
                  <span className="text-xs uppercase tracking-[0.4em] text-slate-400">
                    Units
                  </span>
                  <select
                    value={unitFilter}
                    onChange={(e) => setUnitFilter(e.target.value)}
                    className="bg-transparent text-slate-900 text-sm focus:outline-none"
                  >
                    <option value="all">All</option>
                    {unitOptions.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </Stack>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-white/10 text-sm">
              <thead className="bg-white/5 text-left uppercase tracking-wide text-xs text-slate-300">
                <tr>
                  <th className="px-6 py-3">Nutrient</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">Unit</th>
                  <th className="px-6 py-3">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-white">
                {filteredNutrients.length === 0 ? (
                  <tr>
                    <td
                      className="px-6 py-4 text-center text-slate-400"
                      colSpan={4}
                    >
                      No nutrients match your filters.
                    </td>
                  </tr>
                ) : (
                  filteredNutrients.map((nutrient) => (
                    <tr key={nutrient.id}>
                      <td className="px-6 py-3">
                        <p className="font-semibold">
                          {nutrient?.nutrient?.name || "Unnamed nutrient"}
                        </p>
                        <p className="text-xs text-slate-400">
                          #{nutrient?.nutrient?.number} · Rank{" "}
                          {nutrient?.nutrient?.rank || "–"}
                        </p>
                      </td>
                      <td className="px-6 py-3 font-semibold">
                        {nutrient?.amount ?? "–"}
                      </td>
                      <td className="px-6 py-3 text-slate-300">
                        {nutrient?.nutrient?.unitName || "–"}
                      </td>
                      <td className="px-6 py-3 text-slate-400">
                        {nutrient?.foodNutrientDerivation?.description ||
                          "Not specified"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {moreFoods.length > 0 && (
          <div className="bg-white/5 border border-white/10 rounded-3xl backdrop-blur">
            <div className="px-6 py-5 border-b border-white/10 flex flex-col gap-2">
              <h2 className="text-xl font-semibold">
                Discover more ingredients
              </h2>
              <p className="text-slate-400 text-sm">
                Keep exploring the CaloriesPal food library with a few random
                picks from our database.
              </p>
            </div>
            <div className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-5">
              {moreFoods.map((item) => (
                <Link
                  to={`/foods/${item.slug}`}
                  key={item.slug}
                  className="group rounded-2xl bg-black/30 border border-white/10 p-4 flex flex-col justify-between hover:border-lime-300 transition"
                >
                  <div>
                    <p className="text-sm uppercase tracking-[0.3em] text-slate-400 mb-2">
                      {item.foodCategory?.description || "Ingredient"}
                    </p>
                    <p className="text-lg font-semibold text-white line-clamp-2">
                      {item.description}
                    </p>
                    <p className="text-xs text-slate-400 mt-2">
                      {item.foodClass || "General"}
                    </p>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs text-lime-200">
                    <span>View details</span>
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-lime-300/20 text-lime-200 group-hover:bg-lime-300 group-hover:text-slate-900 transition">
                      →
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default FoodDetail;
